import { useState, useMemo } from 'react';
import { ArrowBackIcon, KeyboardArrowRightIcon } from '@/components/Icons';
import { isAddress, maxUint128, maxUint64 } from 'viem';
import { useCofheAccount } from '@/hooks/useCofheConnection';
import { useCofheTokenDecryptedBalance } from '@/hooks/useCofheTokenDecryptedBalance';
import { useCofheTokenTransfer } from '@/hooks/useCofheTokenTransfer';
import { cn } from '../../../utils/cn';
import { truncateAddress, sanitizeNumericInput } from '../../../utils/utils';
import { TokenIcon } from '../components/TokenIcon';
import { unitToWei } from '@/utils/format';
import { getConfidentialDecimals } from '@/types/token';
import { assert } from 'ts-essentials';
import { CofheTokenConfidentialBalance } from '../components';
import { useCofheTokensWithExistingEncryptedBalances, type ConfidentialToken } from '@/hooks';
import { getStepConfig } from '@/hooks/useCofheEncrypt';
import { cofheHumanizeViemError } from '@/utils/cofheErrors';
import { useOnceTransactionMined } from '@/hooks/useOnceTransactionMined';
import { usePortalModals, usePortalNavigation } from '@/stores';
import { PageContainer } from '../components/PageContainer';
import { PortalModal } from '../modals/types';
import { BalanceType } from '../components/CofheTokenConfidentialBalance';
import { cofheLogger } from '@/utils/debug';

export type SendPageProps = {
  token: ConfidentialToken;
};

declare module '../pagesConfig/types' {
  interface FloatingButtonPagePropsRegistry {
    [FloatingButtonPage.Send]: SendPageProps;
  }
}

export const SendPage: React.FC<SendPageProps> = ({ token: _token }) => {
  const [overriddenToken, setOverriddenToken] = useState<ConfidentialToken | null>(null);

  const token = overriddenToken ?? _token;
  const { navigateBack } = usePortalNavigation();
  const { openModal } = usePortalModals();

  const account = useCofheAccount();
  const {
    encryptAmountAndSendToken,
    data: txHash,
    isPending: isEncryptingOrSendingTx,
    write: { isPending: isSending },
    encryption: {
      stepsState: { lastStep },
      isEncrypting: isEncryptingInput,
    },
  } = useCofheTokenTransfer({
    onError: (error) => {
      const errorMessage =
        cofheHumanizeViemError(error) ?? (error instanceof Error ? error.message : 'Failed to send tokens');
      setError(errorMessage);
      cofheLogger.error('Send tx submit error:', error);
    },
    onMutate: () => {
      setError(null);
      setSuccess(null);
    },
    onSuccess: (hash) => {
      setSuccess(`Transaction sent! Hash: ${truncateAddress(hash)}. Is being mined...`);
      setAmount('');
      setRecipientAddress('');
    },
  });

  useOnceTransactionMined({
    txHash,
    onceMined: (transaction) => {
      if (transaction.status === 'confirmed') {
        setSuccess(`Transaction confirmed! Hash: ${truncateAddress(transaction.hash)}`);
      } else if (transaction.status === 'failed') {
        setError(`Transaction failed! Hash: ${truncateAddress(transaction.hash)}`);
      }
    },
  });

  const { data: { unit: confidentialUnitBalance } = {} } = useCofheTokenDecryptedBalance({
    token,
    accountAddress: account,
  });

  const encryptionProgressLabel = useMemo(() => lastStep?.step && getStepConfig(lastStep).label, [lastStep]);

  const [amount, setAmount] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Validate recipient address
  const isValidAddress = isAddress(recipientAddress);

  // Validate amount
  const isValidAmount = (amount.length > 0 && confidentialUnitBalance && confidentialUnitBalance.gte(amount)) ?? false;

  const handleSend = async () => {
    assert(isAddress(recipientAddress), 'Recipient address is not valid');

    const amountWei = unitToWei(amount, getConfidentialDecimals(token));
    const maxConfidentialAmount = token.extensions.fhenix.confidentialValueType === 'uint128' ? maxUint128 : maxUint64;
    assert(amountWei <= maxConfidentialAmount, 'Amount exceeds maximum supported confidential value');
    assert(account, 'Sender account is required');

    // Use the token transfer hook to send encrypted tokens
    await encryptAmountAndSendToken({
      input: {
        token,
        to: recipientAddress,
        amount: amountWei,
        userAddress: account,
      },
    });
  };

  const handleMaxAmount = () => {
    // Calculate available balance for MAX button
    if (confidentialUnitBalance) setAmount(confidentialUnitBalance.toFixed());
  };

  const { tokensWithExistingEncryptedBalance } = useCofheTokensWithExistingEncryptedBalances();

  const disabled = !isValidAddress || !isValidAmount || isSending || isEncryptingInput;
  return (
    <PageContainer
      header={
        <button
          onClick={navigateBack}
          className="flex items-center gap-1 text-sm hover:opacity-80 transition-opacity mb-2"
        >
          <ArrowBackIcon style={{ fontSize: 16 }} />
          <p className="text-sm font-medium">Shielded Transfer</p>
        </button>
      }
      content={
        <>
          {/* Asset Section */}
          <div className="cofhe-card-bg p-2 border cofhe-card-border">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-medium opacity-70">Asset to be sent</label>
            </div>
            <div className="flex items-center gap-3">
              {/* ConfidentialToken Icon */}
              <TokenIcon logoURI={token.logoURI} alt={token.name} size="md" />

              {/* Amount Input and Symbol on same line, centered with logo */}
              <div className="flex-1 flex items-center gap-1 min-w-0">
                <input
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => {
                    setAmount(sanitizeNumericInput(e.target.value));
                  }}
                  placeholder="0.00"
                  className="flex-1 min-w-0 bg-transparent text-2xl font-bold cofhe-text-primary outline-none placeholder:opacity-50"
                />
                <button
                  onClick={() => {
                    openModal(PortalModal.TokenList, {
                      balanceType: BalanceType.Confidential,
                      tokens: tokensWithExistingEncryptedBalance,
                      title: 'Select token to transfer',
                      onSelectToken: (token) => {
                        setOverriddenToken(token);
                      },
                    });
                  }}
                  className="flex items-center gap-1 text-2xl font-bold cofhe-text-primary hover:opacity-80 transition-opacity whitespace-nowrap flex-shrink-0"
                >
                  <span>{token.symbol}</span>
                  <KeyboardArrowRightIcon className="w-5 h-5 cofhe-text-primary opacity-60 flex-shrink-0" />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-3 -mt-1 text-xs opacity-70">
              {/* Invisible placeholder to align with icon above */}
              <div className="w-10 flex-shrink-0" />

              {/* Available text and MAX button */}
              <div className="flex-1 flex items-center justify-start min-w-0 gap-2">
                <span className="text-xs opacity-70">Available </span>
                <CofheTokenConfidentialBalance
                  token={token}
                  showSymbol={true}
                  size="sm"
                  decimalPrecision={5}
                  className="text-xs opacity-70 font-medium"
                />
                <button
                  onClick={handleMaxAmount}
                  className="cofhe-max-button text-xxxs ml-1 font-medium px-0.5 py-0.2 rounded"
                >
                  MAX
                </button>
              </div>
            </div>
          </div>

          {/* Down Arrow */}
          <div className="flex justify-center">
            <div className="w-8 h-8 rounded-full cofhe-icon-bg flex items-center justify-center">
              <span className="text-lg">↓</span>
            </div>
          </div>

          {/* Recipient Address */}
          <div className="cofhe-card-bg p-4 border cofhe-card-border">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-medium opacity-70">Recipient Address:</label>
            </div>
            <input
              type="text"
              value={recipientAddress}
              onChange={(e) => setRecipientAddress(e.target.value)}
              placeholder="0x..."
              className={cn(
                'w-full bg-transparent cofhe-text-primary outline-none border-b pb-2 px-2',
                'placeholder:opacity-50',
                isValidAddress ? 'border-green-500' : recipientAddress ? 'border-red-500' : 'cofhe-card-border'
              )}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
              <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
            </div>
          )}

          {/* Encryption Status */}
          {isEncryptingOrSendingTx && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                {isEncryptingInput ? encryptionProgressLabel || 'Encrypting amount...' : 'Sending transaction...'}
              </p>
            </div>
          )}
        </>
      }
      footer={
        <button
          onClick={handleSend}
          disabled={disabled}
          className={cn(
            'cofhe-send-button w-full py-3 px-4 font-small',
            'flex items-center justify-center gap-2',
            disabled ? 'cofhe-send-button-disabled' : 'cofhe-send-button-enabled'
          )}
        >
          <span>Send</span>
          <span className="text-lg">↗</span>
        </button>
      }
    />
  );
};
